import {
  createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, useRouterState,
  type RouteComponent,
} from '@tanstack/react-router';
import { AppShell } from '../components/layout/AppShell.js';

/**
 * Every page is its own download, fetched when it is first opened -- or when a link to it is
 * hovered (defaultPreload: 'intent'), so a click rarely waits. All of them used to be one 2.1 MB
 * file that every visit downloaded before showing anything, the flow editor and the browser phone
 * included (stability review P1, 4 November 2026).
 */
const LivePage = lazyRouteComponent(() => import('../features/live/LivePage.js'), 'LivePage');
const LiveQueuesPage = lazyRouteComponent(() => import('../features/live/LiveQueuesPage.js'), 'LiveQueuesPage');
const WallboardPage = lazyRouteComponent(() => import('../features/live/WallboardPage.js'), 'WallboardPage');
const DashboardPage = lazyRouteComponent(() => import('../features/dashboard/DashboardPage.js'), 'DashboardPage');
const AccountsPage = lazyRouteComponent(() => import('../features/accounts/AccountsPage.js'), 'AccountsPage');
const AccountDetailPage = lazyRouteComponent(() => import('../features/accounts/AccountDetailPage.js'), 'AccountDetailPage');
const PlatformUsersPage = lazyRouteComponent(() => import('../features/accounts/PlatformUsersPage.js'), 'PlatformUsersPage');
const AccountSecurityPage = lazyRouteComponent(() => import('../features/account/AccountSecurityPage.js'), 'AccountSecurityPage');
const ExtensionTypesPage = lazyRouteComponent(() => import('../features/products/ExtensionTypesPage.js'), 'ExtensionTypesPage');
const CallPlansPage = lazyRouteComponent(() => import('../features/products/CallPlansPage.js'), 'CallPlansPage');
const CarriersPage = lazyRouteComponent(() => import('../features/carriers/CarriersPage.js'), 'CarriersPage');
const NumberInventoryPage = lazyRouteComponent(() => import('../features/carriers/NumberInventoryPage.js'), 'NumberInventoryPage');
const HandsetCatalogPage = lazyRouteComponent(() => import('../features/provisioning/HandsetCatalogPage.js'), 'HandsetCatalogPage');
const ActivityPage = lazyRouteComponent(() => import('../features/accounts/ActivityPage.js'), 'ActivityPage');
const JobsPage = lazyRouteComponent(() => import('../features/accounts/JobsPage.js'), 'JobsPage');
const RecordingStoragePage = lazyRouteComponent(() => import('../features/accounts/RecordingStoragePage.js'), 'RecordingStoragePage');
const ProviderKeysPage = lazyRouteComponent(() => import('../features/accounts/ProviderKeysPage.js'), 'ProviderKeysPage');
const TeamsConnectedPage = lazyRouteComponent(() => import('../features/manage/TeamsConnectedPage.js'), 'TeamsConnectedPage');
const NodesPage = lazyRouteComponent(() => import('../features/accounts/NodesPage.js'), 'NodesPage');
const SipSecurityPage = lazyRouteComponent(() => import('../features/accounts/SipSecurityPage.js'), 'SipSecurityPage');
const LogWatchPage = lazyRouteComponent(() => import('../features/accounts/LogWatchPage.js'), 'LogWatchPage');
const SettingsPage = lazyRouteComponent(() => import('../features/accounts/SettingsPage.js'), 'SettingsPage');
const BillingEntityPage = lazyRouteComponent(() => import('../features/billing/BillingEntityPage.js'), 'BillingEntityPage');
const InvoicesPage = lazyRouteComponent(() => import('../features/billing/InvoicesPage.js'), 'InvoicesPage');
const BillingRunsPage = lazyRouteComponent(() => import('../features/billing/BillingRunsPage.js'), 'BillingRunsPage');
const ResellersPage = lazyRouteComponent(() => import('../features/accounts/ResellersPage.js'), 'ResellersPage');
const ExtensionsPage = lazyRouteComponent(() => import('../features/extensions/ExtensionsPage.js'), 'ExtensionsPage');
const CdrsPage = lazyRouteComponent(() => import('../features/cdrs/CdrsPage.js'), 'CdrsPage');
const BillingPage = lazyRouteComponent(() => import('../features/billing/BillingPage.js'), 'BillingPage');
const RateSheetsPage = lazyRouteComponent(() => import('../features/billing/BillingPage.js'), 'RateSheetsPage');
const PlaceholderPage = lazyRouteComponent(() => import('../features/misc/PlaceholderPage.js'), 'PlaceholderPage');
const ManageAccountPage = lazyRouteComponent(() => import('../features/manage/ManageAccountPage.js'), 'ManageAccountPage');
const AgentWorkspace = lazyRouteComponent(() => import('../features/agent/AgentWorkspace.js'), 'AgentWorkspace');
const AttendancePage = lazyRouteComponent(() => import('../features/agent/AttendancePage.js'), 'AttendancePage');
const FraudPage = lazyRouteComponent(() => import('../features/fraud/FraudPage.js'), 'FraudPage');
const RicaReviewsPage = lazyRouteComponent(() => import('../features/rica/RicaPage.js'), 'RicaReviewsPage');

/**
 * The root renders the console shell for every route EXCEPT /manage/*.
 *
 * Account configuration is a full-window working session on one customer, so it
 * gets its own chrome and its own browser tab. Wrapping it in the console's
 * global navigation would invite wandering out of a half-finished change.
 */
const rootRoute = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  // The workspace sits in the console's frame (owner, 3 October 2026: "where did the side panel
  // go???"): an agent moves between it, their calls and their extension all day.
  if (path.startsWith('/manage/')) return <Outlet />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const route = (path: string, component: RouteComponent) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

/** The platform's own phone templates: the same screen as an account's, with no account. */
const PlatformTemplatesPage = lazyRouteComponent(() => import('../features/provisioning/TemplatesPage.js')
  .then((m) => ({ default: () => <m.TemplatesPage tenantId={null} /> })));

/**
 * Route tree mirrors the navigation, and per-account detail lives under
 * /accounts/$accountId rather than as top-level screens.
 *
 * That is what makes a support link unambiguous: the URL names the customer, so
 * pasting one cannot land a colleague on a different account than the one being
 * discussed.
 */
const routeTree = rootRoute.addChildren([
  route('/', DashboardPage),

  /**
   * Realtime.
   *
   * /live is kept because links to it are already in circulation — a support
   * conversation that pasted one should not dead-end. It renders the same page
   * rather than redirecting, so the URL a colleague opens still works.
   */
  route('/realtime/extensions', LivePage),
  route('/realtime/queues', LiveQueuesPage),
  route('/realtime/wallboard', WallboardPage),
  route('/realtime/wallboard/$', WallboardPage),
  route('/live', LivePage),
  route('/activity', ActivityPage),

  // Accounts, and everything about one account behind its id.
  route('/accounts', AccountsPage),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/accounts/$accountId',
    component: AccountDetailPage,
  }),
  // Below an account is a path, never a query string: /accounts/meridian/extensions/1001.
  // The page reads it with useSubroute (lib/subroute.tsx).
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/accounts/$accountId/$',
    component: AccountDetailPage,
  }),
  // Full account configuration — opened in its own browser tab from the console.
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/manage/$tenantId',
    component: ManageAccountPage,
  }),
  // /manage/meridian/extensions/1001/phone: the section, then whatever it shows.
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/manage/$tenantId/$',
    component: ManageAccountPage,
  }),

  // The agent workspace: the browser phone and the calls around it (docs/36 SS8).
  route('/agent', AgentWorkspace),
  // What the workspace's shifts add up to. Inside the console's chrome, because it is read
  // between other things rather than worked in.
  route('/realtime/attendance', AttendancePage),

  // Toll fraud (docs/39): holds and alerts across whatever this person can see.
  route('/fraud', FraudPage),
  route('/rica', RicaReviewsPage),
  route('/resellers', ResellersPage),
  route('/users', PlatformUsersPage),
  // The signed-in person's own security: two-step verification.
  route('/account/security', AccountSecurityPage),

  // Products
  route('/products/extension-types', ExtensionTypesPage),
  route('/products/call-plans', CallPlansPage),
  route('/products/rate-sheets', RateSheetsPage),
  // One sheet, by its name: /products/rate-sheets/switch-telecom-retail.
  route('/products/rate-sheets/$', RateSheetsPage),

  // Inventory
  route('/inventory/carriers', CarriersPage),
  route('/inventory/numbers', NumberInventoryPage),
  route('/inventory/handsets', HandsetCatalogPage),
  // Phone templates every account can use, the open one by its slug: /inventory/templates/reception.
  route('/inventory/templates', PlatformTemplatesPage),
  route('/inventory/templates/$', PlatformTemplatesPage),

  // Billing
  route('/billing/runs', BillingRunsPage),
  route('/billing/invoices', InvoicesPage),
  // An issued invoice by its number: /billing/invoices/INV00012.
  route('/billing/invoices/$', InvoicesPage),
  route('/billing/setup', BillingEntityPage),
  // Its first name, kept so an old link still lands.
  route('/billing/entity', BillingEntityPage),

  // Platform
  route('/platform/nodes', NodesPage),
  route('/platform/jobs', JobsPage),
  route('/platform/provider-keys', ProviderKeysPage),
  // Back from Microsoft's consent page (Connect Microsoft 365, teams/microsoft.ts).
  route('/teams/connected', TeamsConnectedPage),
  route('/platform/recording-storage', RecordingStoragePage),
  route('/platform/sip-security', SipSecurityPage),
  route('/platform/log-watch', LogWatchPage),
  route('/platform/settings', SettingsPage),

  // Tenant-scoped users: their own PBX, no account list to drill through.
  route('/my/extensions', ExtensionsPage),
  route('/my/extensions/$', ExtensionsPage),
  route('/my/calls', CdrsPage),
  route('/my/billing', BillingPage),
  // The tab below it: /my/billing/payments is where PayFast sends a payer back to.
  route('/my/billing/$', BillingPage),
]);

// A new page (a tab, a section, an extension's tab) opens at the top; Back returns to where you were
// (owner, 21 October 2026: "clicking on a tab should reload it at the top").
export const router = createRouter({ routeTree, defaultPreload: 'intent', scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}
